import AddAutomaticallyButton from './AddAutomaticallyButton';
import AddNewTableEntryButton from './AddNewTableEntryButton';
import TrackerTableDownloadButton from './TrackerTableDownloadButton';
import { Button } from '@mui/material';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { TrackerDataType } from '../../shared/constants/constants';
import QueryAdderDrawer from '../QueryAdder/QueryAdderDrawer';
import BulkEditQueryAdder from '../QueryAdder/BulkEditQueryAdder';
import EditPencilIcon from '../TrackerFilter/Icons/EditPencilIcon';

const trackerEventHandlerPropType = PropTypes.shape({
  type: PropTypes.oneOf([
    TrackerDataType.MEDIA_INTERACTION,
    TrackerDataType.SERVICE,
    TrackerDataType.ENGAGEMENT,
    TrackerDataType.CONTENT_LOG,
  ]).isRequired,
  flattenForExport: PropTypes.func.isRequired,
  newItemModalTitle: PropTypes.string,
});

export const TrackerTableActionButtons = (props) => {
  const {
    eventHandler,
    dropdownTable,
    currMonth,
    rows,
    isBulkEdit,
    selectedBulkEditRowIds,
    selectedBulkEditRowsCount,
    onSwitchToBulkEditView,
    onSwitchToMonthlyView,
    hasAutoAdd = true,
    detailedExportContext,
  } = props;

  const [openBulkEditPopup, setOpenBulkEditPopup] = useState(false);

  const getComponentColor = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return 'mediaInteraction';
      case TrackerDataType.SERVICE:
        return 'primary';
      case TrackerDataType.ENGAGEMENT:
        return 'primaryLighter';
      case TrackerDataType.CONTENT_LOG:
        return 'primaryLight';
      default:
        return 'primary';
    }
  };

  const getRowType = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return selectedBulkEditRowsCount > 1 ? 'Interactions' : 'Interaction';
      case TrackerDataType.SERVICE:
        return selectedBulkEditRowsCount > 1 ? 'Services' : 'Service';
      case TrackerDataType.ENGAGEMENT:
        return selectedBulkEditRowsCount > 1 ? 'Engagements' : 'Engagement';
      case TrackerDataType.CONTENT_LOG:
        return selectedBulkEditRowsCount > 1 ? 'Content Logs' : 'Content Log';
      default:
        return selectedBulkEditRowsCount > 1 ? 'Items' : 'Item';
    }
  };

  if (!isBulkEdit) {
    return (
      <>
        {hasAutoAdd && <AddAutomaticallyButton eventHandler={eventHandler} />}
        <AddNewTableEntryButton dropdownTable={dropdownTable} eventHandler={eventHandler} currMonth={currMonth} />
        <Button
          variant="contained"
          color={getComponentColor()}
          sx={{ height: '32px' }}
          onClick={() => onSwitchToBulkEditView()}
          data-cy="openBulkEditViewButton"
          startIcon={<EditPencilIcon />}
        >
          Multiple
        </Button>
        <TrackerTableDownloadButton
          rows={rows}
          dropdowns={dropdownTable}
          flattenerCallback={eventHandler.flattenForExport}
          filename={eventHandler.type}
          color={getComponentColor()}
          detailedExportContext={detailedExportContext}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="contained"
        color={getComponentColor()}
        sx={{ height: '32px' }}
        disabled={selectedBulkEditRowsCount === 0}
        onClick={() => setOpenBulkEditPopup(true)}
        data-cy="openBulkEditPopupButton"
      >
        Edit {selectedBulkEditRowsCount} {getRowType()}
      </Button>
      <Button
        variant="outlined"
        color={getComponentColor()}
        sx={{ height: '32px' }}
        onClick={() => onSwitchToMonthlyView()}
        data-cy="cancelBulkEditViewButton"
      >
        Cancel
      </Button>
      <QueryAdderDrawer openForm={openBulkEditPopup} setOpenForm={setOpenBulkEditPopup}>
        <BulkEditQueryAdder
          selectedBulkEditRowIds={selectedBulkEditRowIds}
          eventHandler={eventHandler}
          dropdownTable={dropdownTable}
          currMonth={currMonth}
          rowType={getRowType()}
          onClose={() => setOpenBulkEditPopup(false)}
        />
      </QueryAdderDrawer>
    </>
  );
};

TrackerTableActionButtons.propTypes = {
  eventHandler: trackerEventHandlerPropType.isRequired,
  dropdownTable: PropTypes.object.isRequired,
  currMonth: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cells: PropTypes.object,
    })
  ).isRequired,
  isBulkEdit: PropTypes.bool.isRequired,
  selectedBulkEditRowIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  selectedBulkEditRowsCount: PropTypes.number.isRequired,
  onSwitchToBulkEditView: PropTypes.func.isRequired,
  onSwitchToMonthlyView: PropTypes.func.isRequired,
  hasAutoAdd: PropTypes.bool,
  detailedExportContext: PropTypes.object,
};
